import React, { useState, useEffect } from 'react';
import { Card, Space, Spin, message } from 'antd';
import moment from 'moment';
import {
  getCircuitosByDay,
  getCircuitosByVendedor,
  getEncuestas,
  getRespuestasEncuesta,
  verificarRespuesta,
  verificarRespuestasMasivo,
  getConteoRespuestasNoVerificadas
} from '../../services/verificarEncuestas';
import Layouts from '../../components/Layouts';
import Filtros from './components/Filtros';
import TablaRespuestas from './components/TablaRespuestas';
import { useSelector } from 'react-redux';

const VerificarEncuestas = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [circuitos, setCircuitos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [encuestas, setEncuestas] = useState([]);
  const [selectedEncuesta, setSelectedEncuesta] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [conteoRespuestas, setConteoRespuestas] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  const userRoleID = useSelector((state) => state.userdata.role.name);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  
  useEffect(() => {
    loadEncuestas();
  }, []);

  useEffect(() => {
    if (selectedEncuesta && selectedDate) {
      const dayNumber = moment(selectedDate).isoWeekday();
      setSelectedDay(dayNumber);
      loadCircuitosByDay(dayNumber);
    }
  }, [selectedEncuesta, selectedDate]);

  useEffect(() => {
    if (circuitos.length > 0 && selectedEncuesta) {
      loadVendedoresByDay();
    }
  }, [circuitos, selectedEncuesta]);

  useEffect(() => {
    if (selectedVendedor && selectedEncuesta) {
      loadClientesByVendedor();
    }
  }, [selectedVendedor, selectedEncuesta]);

  useEffect(() => {
    if (selectedDate && selectedVendedor && selectedEncuesta && selectedCliente) {
      loadRespuestas();
    } else {
      setRespuestas([]);
      setPagination({
        current: 1,
        pageSize: 25,
        total: 0
      });
    }
  }, [selectedDate, selectedVendedor, selectedCliente, selectedEncuesta, pagination.current, pagination.pageSize]);

  const loadEncuestas = async () => {
    try {
      setLoading(true);
      const data = await getEncuestas();
      setEncuestas(data);
      
      const conteos = {};
      for (const encuesta of data) {
        const count = await getConteoRespuestasNoVerificadas(encuesta.id);
        conteos[encuesta.id] = count;
      }
      setConteoRespuestas(conteos);
    } catch (error) {
      console.error('Error al cargar encuestas:', error);
      message.error('Error al cargar encuestas');
    } finally {
      setLoading(false);
    }
  };

  const loadCircuitosByDay = async (day) => {
    try {
      setLoading(true);
      const data = await getCircuitosByDay(day, userRoleID, userBodega);
      setCircuitos(data);
    } catch (error) {
      console.error('Error al cargar circuitos:', error);
      message.error('Error al cargar circuitos');
    } finally {
      setLoading(false);
    }
  };

  const loadVendedoresByDay = () => {
    const vendedoresUnicos = circuitos.reduce((acc, circuito) => {
      if (circuito.vendedor && !acc.find(v => v.id === circuito.vendedor.id)) {
        acc.push({
          id: circuito.vendedor.id,
          nombre: `${circuito.vendedor.first_name} ${circuito.vendedor.last_name}`
        });
      }
      return acc;
    }, []);
    setVendedores(vendedoresUnicos);
  };

  const loadClientesByVendedor = async () => {
    try {
      setLoading(true);
      const data = await getCircuitosByVendedor(selectedVendedor, userRoleID, userBodega);
      
      const respuestas = await getRespuestasEncuesta({
        vendedorId: selectedVendedor,
        encuestaId: selectedEncuesta
      });

      const clientesConRespuestas = new Set(respuestas.data.map(r => r.clienteId));
      
      const clientesUnicos = data.reduce((acc, circuito) => {
        if (circuito.clientes) {
          circuito.clientes.forEach(cliente => {
            if (cliente.clientes_id && 
                clientesConRespuestas.has(cliente.clientes_id.id) && 
                !acc.find(c => c.id === cliente.clientes_id.id)) {
              acc.push({
                id: cliente.clientes_id.id,
                nombre: `${cliente.clientes_id.first_name} ${cliente.clientes_id.last_name}`
              });
            }
          });
        }
        return acc;
      }, []);
      
      setClientes(clientesUnicos);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      message.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadRespuestas = async () => {
    try {
      setLoading(true);
      const params = {
        vendedorId: selectedVendedor,
        clienteId: selectedCliente,
        encuestaId: selectedEncuesta,
        page: pagination.current,
        limit: pagination.pageSize
      };
      const { data, total } = await getRespuestasEncuesta(params);
      setRespuestas(data);
      setPagination({
        ...pagination,
        total: total
      });
    } catch (error) {
      console.error('Error al cargar respuestas:', error);
      message.error('Error al cargar respuestas');
      setRespuestas([]);
      setPagination({
        ...pagination,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarRespuesta = async (respuestaId) => {
    try {
      setLoading(true);
      await verificarRespuesta(respuestaId);
      setRespuestas(respuestas.map(resp => 
        resp.id === respuestaId ? { ...resp, verificada: true } : resp
      ));
      message.success('Respuesta verificada correctamente');
      
      // Actualizar conteo de respuestas no verificadas
      loadEncuestas();
    } catch (error) {
      console.error('Error verificando respuesta:', error);
      message.error('Error al verificar la respuesta');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarTodas = async () => {
    try {
      setLoading(true);
      const respuestasNoVerificadas = respuestas
        .filter(resp => !resp.verificada)
        .map(resp => resp.id);
      
      if (respuestasNoVerificadas.length === 0) {
        message.info('No hay respuestas para verificar');
        return;
      }

      await verificarRespuestasMasivo(respuestasNoVerificadas);
      setRespuestas(respuestas.map(resp => ({ ...resp, verificada: true })));
      message.success('Todas las respuestas verificadas correctamente');
      
      // Actualizar conteo de respuestas no verificadas
      loadEncuestas();
    } catch (error) {
      console.error('Error verificando respuestas:', error);
      message.error('Error al verificar las respuestas');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedVendedor(null);
    setSelectedCliente(null);
    setVendedores([]);
    setClientes([]);
    setRespuestas([]);
  };

  const handleVendedorChange = (value) => {
    setSelectedVendedor(value);
    setSelectedCliente(null);
    setRespuestas([]);
  };
  
  const handleEncuestaChange = (value) => {
    setSelectedEncuesta(value);
    setSelectedDate(null);
    setSelectedDay(null);
    setSelectedVendedor(null);
    setSelectedCliente(null);
    setCircuitos([]);
    setVendedores([]);
    setClientes([]);
    setRespuestas([]);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      current: page,
      pageSize: pageSize,
      total: pagination.total
    });
  };

  return (
    <Layouts>
      <Card title="Verificar Encuestas">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Filtros
            selectedDate={selectedDate}
            selectedVendedor={selectedVendedor}
            selectedCliente={selectedCliente}
            selectedEncuesta={selectedEncuesta}
            vendedores={vendedores}
            clientes={clientes}
            encuestas={encuestas}
            conteoRespuestas={conteoRespuestas}
            respuestas={respuestas}
            onDateChange={handleDateChange}
            onVendedorChange={handleVendedorChange}
            onClienteChange={setSelectedCliente}
            onEncuestaChange={handleEncuestaChange}
            onVerificarTodas={handleVerificarTodas}
          />

          <Spin spinning={loading}>
            <TablaRespuestas
              loading={loading}
              respuestas={respuestas}
              onVerificarRespuesta={handleVerificarRespuesta}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
            />
          </Spin>
        </Space>
      </Card>
    </Layouts>
  );
};

export default VerificarEncuestas;